import React, { useMemo } from 'react'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { VStack, HStack, Text } from 'govwell-ui'
import { twMerge } from 'tailwind-merge'
import { InspectionAvailabilityWindowDataFragment } from 'types/graphql'

import { DateFormats } from 'src/utils/date'
import { DAYS_OF_WEEK } from 'src/utils/daysOfWeek'

// Add UTC plugin to dayjs
dayjs.extend(utc)

type Props = {
  windows:
    | InspectionAvailabilityWindowDataFragment[]
    | { dayOfWeek: number; startTime: string; endTime: string }[]
  title?: string
  closedLabel?: string
  type?: 'list' | 'tiles'
}

export const InspectionHoursDisplay = ({
  windows,
  title,
  closedLabel = 'No availability',
  type = 'list',
}: Props) => {
  const hoursByDay = useMemo(() => {
    const result: Map<number, string> = new Map()

    DAYS_OF_WEEK.forEach((day) => {
      const dayWindows = windows
        .filter((window) => window.dayOfWeek === day.dayOfWeek)
        .sort((a, b) => {
          const aStart = dayjs.utc(a.startTime)
          const bStart = dayjs.utc(b.startTime)
          return aStart.isBefore(bStart) ? -1 : 1
        })

      if (dayWindows.length === 0) {
        result.set(day.dayOfWeek, closedLabel)
        return
      }

      // Group consecutive windows into ranges
      const ranges: string[] = []
      let currentStart: dayjs.Dayjs | null = null
      let currentEnd: dayjs.Dayjs | null = null

      dayWindows.forEach((window, index) => {
        const windowStart = dayjs.utc(window.startTime)
        const windowEnd = dayjs.utc(window.endTime)

        if (!currentStart) {
          currentStart = windowStart
          currentEnd = windowEnd
        } else {
          // Check if this window is consecutive to the current range
          if (windowStart.isSame(currentEnd)) {
            // Consecutive window, extend the range
            currentEnd = windowEnd
          } else {
            // Gap found, save current range and start new one
            const startDisplay = currentStart.format(DateFormats.TimeWithAMPMLowercase)
            const endDisplay = currentEnd?.format(DateFormats.TimeWithAMPMLowercase) || ''
            ranges.push(`${startDisplay} - ${endDisplay}`)

            currentStart = windowStart
            currentEnd = windowEnd
          }
        }

        // If this is the last window, save the current range
        if (index === dayWindows.length - 1 && currentStart && currentEnd) {
          const startDisplay = currentStart.format(DateFormats.TimeWithAMPMLowercase)
          const endDisplay = currentEnd.format(DateFormats.TimeWithAMPMLowercase)
          ranges.push(`${startDisplay} - ${endDisplay}`)
        }
      })

      result.set(day.dayOfWeek, ranges.join(', '))
    })

    return result
  }, [closedLabel, windows])

  if (type === 'tiles') {
    return (
      <VStack gap={2} align="start" width="100%">
        {title && <Text.H4>{title}</Text.H4>}
        <div className="grid w-full gap-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {DAYS_OF_WEEK.map((day) => (
            <div
              key={day.dayOfWeek}
              className="flex flex-col gap-1 rounded-md border border-neutral-200 bg-neutral-50 p-4"
            >
              <Text.Body>{day.label}</Text.Body>
              <Text.Body
                className={hoursByDay.get(day.dayOfWeek) === closedLabel ? 'text-gray-500' : ''}
              >
                {hoursByDay.get(day.dayOfWeek)}
              </Text.Body>
            </div>
          ))}
        </div>
      </VStack>
    )
  }

  return (
    <VStack gap={2} align="start" width="100%">
      {title && <Text.H4>{title}</Text.H4>}
      <VStack gap={1} align="start" width="100%">
        {DAYS_OF_WEEK.map((day) => (
          <HStack key={day.dayOfWeek} gap={2} align="center">
            <Text.Body className="min-w-[45px]">{day.short.toUpperCase()}:</Text.Body>
            <Text.Body
              className={twMerge(
                hoursByDay.get(day.dayOfWeek) === closedLabel ? 'text-gray-500' : '',
                hoursByDay.get(day.dayOfWeek) === closedLabel ? '' : 'font-bold'
              )}
            >
              {hoursByDay.get(day.dayOfWeek)}
            </Text.Body>
          </HStack>
        ))}
      </VStack>
    </VStack>
  )
}

export default React.memo(InspectionHoursDisplay)
